import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useAuth } from "react-oidc-context";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";

import { getEnv } from "~/utils";

import App from "./App";
import AuthenticationStore from "./auth";
import ErrorBoundaryDisplay from "./components/ErrorBoundaryDisplay";
import UserMembershipProvider from "./contexts/UserMembershipContext";
import { routes } from "./routes";

const SignIn = React.lazy(() => import("./pages/Authentication/SignIn"));

const AllSystems = React.lazy(() => import("./pages/Main/AllSystems"));
const AllModelsNew = React.lazy(() => import("./pages/Main/AllModelsNew/AllModelsNew"));
const Jobs = React.lazy(() => import("./pages/Main/Jobs"));
const Settings = React.lazy(() => import("./pages/Main/Settings"));

const Comment = React.lazy(() => import("./pages/Main/CommentPage"));
const Artifact = React.lazy(() => import("./pages/Main/Artifact"));
const SystemDetailsPage = React.lazy(() => import("./pages/Main/System"));
const FunctionAuthCallback = React.lazy(() => import("./pages/Main/FunctionAuthCallback"));

const AdminPageLayout = React.lazy(() => import("./pages/Main/Admin/admin-page-layout"));
const AdminUsersPage = React.lazy(() => import("./pages/Main/Admin/users-page"));
const AdminControlTagsPage = React.lazy(() => import("./pages/Main/Admin/control-tags-page"));
const AdminInfosecPage = React.lazy(() => import("./pages/Main/Admin/infosec-page"));
const AdminIntegrationsPage = React.lazy(() => import("./pages/Main/Admin/integrations-page"));
const AdminEncryptionPage = React.lazy(() => import("./pages/Main/Admin/encryption-page"));
const AdminUsagePage = React.lazy(() => import("./pages/Main/Admin/usage-page"));
const AgentManagementPage = React.lazy(() => import("./pages/Main/AgentManagement/agent-management-page"));

const PageNotFound = React.lazy(() => import("./components/PageNotFound"));

function UserMemPagesLayout() {
  return (
    <UserMembershipProvider>
      <Outlet />
    </UserMembershipProvider>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryDisplay}>
      <App />
    </ErrorBoundary>
  );
}

function AuthManager() {
  const location = useLocation();
  const auth = useAuth();
  const AS = AuthenticationStore();
  const redirectPath = AS.consumeRedirectPath();

  return auth.isAuthenticated ? (
    <>{redirectPath ? <Navigate to={redirectPath} replace /> : <Outlet />}</>
  ) : (
    <Navigate to={routes.SignIn} state={{ from: location }} replace />
  );
}

const PageTitle = ({ title }: { title: string }) => {
  const location = useLocation();
  useEffect(() => {
    const baseTitle = "Istari Digital";
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;
  }, [location.pathname, title]);
  return null;
};

export default PageTitle;

export const router = createBrowserRouter(
  [
    {
      path: routes.FunctionAuthCallback,
      element: (
        <>
          <PageTitle title="Function Authentication" />
          <FunctionAuthCallback />
        </>
      ),
    },
    {
      path: routes.Root,
      element: <AppWithErrorBoundary />,
      children: [
        {
          path: routes.SignIn,
          element: (
            <>
              <PageTitle title="Sign In" />
              <SignIn />
            </>
          ),
        },
        {
          element: <AuthManager />,
          children: [
            {
              element: <UserMemPagesLayout />,
              children: [
                {
                  path: "/",
                  element: <Navigate to={routes.Systems} />,
                },
                {
                  path: "/home",
                  element: <Navigate to={routes.Systems} replace />,
                },
                {
                  path: routes.Systems,
                  element: (
                    <>
                      <PageTitle title="Systems" />
                      <AllSystems />
                    </>
                  ),
                },
                {
                  path: routes.Files,
                  element: (
                    <>
                      <PageTitle title="Files" />
                      <AllModelsNew />
                    </>
                  ),
                },
                {
                  path: `${routes.Files}/:modelId/:revisionId?`,
                  element: (
                    <>
                      <PageTitle title="Files" />
                      <AllModelsNew />
                    </>
                  ),
                },
                {
                  path: `${routes.Files}/:modelId/:revisionId?/artifact/:artifactId/:artifactRevisionId?`,
                  element: (
                    <>
                      <PageTitle title="Files" />
                      <AllModelsNew />
                    </>
                  ),
                },
                {
                  path: routes.Jobs,
                  element: (
                    <>
                      <PageTitle title="Jobs" />
                      <Jobs />
                    </>
                  ),
                },
                {
                  path: routes.JobDetails,
                  element: (
                    <>
                      <PageTitle title="Job Details" />
                      <Jobs />
                    </>
                  ),
                },
                {
                  path: routes.Admin,
                  element: (
                    <>
                      <PageTitle title="Admin" />
                      <AdminPageLayout />
                    </>
                  ),
                  children: [
                    {
                      index: true,
                      element: <Navigate to={routes.AdminUsers} replace />,
                    },
                    {
                      path: routes.AdminUsers,
                      element: (
                        <>
                          <PageTitle title="Users Management" />
                          <AdminUsersPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminControlTags,
                      element: (
                        <>
                          <PageTitle title="Control Tags" />
                          <AdminControlTagsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminInfosec,
                      element: (
                        <>
                          <PageTitle title="Infosec Level Management" />
                          <AdminInfosecPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminIntegrations,
                      element: (
                        <>
                          <PageTitle title="Integrations" />
                          <AdminIntegrationsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminEncryption,
                      element: (
                        <>
                          <PageTitle title="Encryption" />
                          <AdminEncryptionPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminUsage,
                      element: (
                        <>
                          <PageTitle title="Usage Metrics" />
                          <AdminUsagePage />
                        </>
                      ),
                    },
                  ],
                },
                {
                  path: "/agent-management",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agents",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agent",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agent-management",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: routes.Agents,
                  element: (
                    <>
                      <PageTitle title="Agents" />
                      <AgentManagementPage />
                    </>
                  ),
                },
                {
                  path: routes.Settings,
                  element: (
                    <>
                      <PageTitle title="Settings" />
                      <Settings />
                    </>
                  ),
                },
                {
                  path: routes.SystemDetails,
                  element: (
                    <>
                      <PageTitle title="System Details" />
                      <SystemDetailsPage />
                    </>
                  ),
                },
                {
                  path: routes.Artifact,
                  element: (
                    <>
                      <PageTitle title="Artifact" />
                      <Artifact />
                    </>
                  ),
                },
                {
                  path: routes.CommentDetails,
                  element: (
                    <>
                      <PageTitle title="Comment Details" />
                      <Comment />
                    </>
                  ),
                },
                {
                  path: routes.NotFound,
                  element: (
                    <>
                      <PageTitle title="Page Not Found" />
                      <PageNotFound />
                    </>
                  ),
                },
                {
                  path: "*",
                  element: (
                    <>
                      <PageTitle title="Page Not Found" />
                      <PageNotFound />
                    </>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
    basename: getEnv("BASE_URL"),
  },
);
